import {
  Box,
  Button,
  Card,
  Inline,
  Loader,
  SelectableOptionT,
} from "@compono/ui";
import { CloseSolidIcon } from "@compono/ui-icons";
import React, { useMemo, useState } from "react";
import { useApolloClients } from "~/components/ApolloClients";
import { FilterPill } from "~/components/FilterPill";
import {
  CulturePerceptionQuery,
  useCulturePerceptionQuery,
} from "~/lib/federationGateway/platformGraphql";
import { Filter, FilterType } from "~/types/filter";
import { EngagementContent } from ".";
import NotFoundImage from "../../../assets/images/not-found-image";
import ErrorMessage from "../../../components/ErrorMessage/ErrorMessage";
import { AssessmentType, Campaign } from "../../../types";
import { FiltersSelect } from "../Explorer";
import {
  FilterableCampaign,
  InsightsFilterInput,
  InsightsFilterType,
} from "../Explorer/types";

type CulturePerception = CulturePerceptionQuery["culturePerception"];

/** The `FiltersSelect` top-level labels this tab can filter on, and the filter each one adds. */
const DEMOGRAPHIC_FILTER_TYPES: Record<string, FilterType> = {
  Divisions: FilterType.Division,
  "Office location": FilterType.OfficeLocation,
  "Job level": FilterType.JobLevel,
};

/**
 * Remount it per campaign (`key={campaign.sk}`) so the filters picked for one campaign are never
 * sent with the next one's assessment.
 */
const Engagement = ({
  selectedCampaign,
  closedCampaigns,
}: {
  selectedCampaign?: Campaign;
  closedCampaigns: Campaign[];
}) => {
  const [filters, setFilters] = useState<Filter[]>([]);

  const { culturePerception, loading, error } = useCulturePerception(
    findPerceptionAssessmentSk(selectedCampaign),
    filters,
  );

  // Only until the first answer arrives: a cache-and-network refresh keeps showing what is cached
  // rather than blanking the tab and the filter menu with it.
  if (loading && !culturePerception) {
    return (
      <Box sx={{ margin: "20px" }}>
        <Inline alignX="center">
          <Loader size="lg" label="loading" hideLabel />
        </Inline>
      </Box>
    );
  }

  const addFilter = (filter: Filter) => {
    const exists = filters.find(
      (existing) =>
        existing.type === filter.type && existing.value === filter.value,
    );
    if (!exists) setFilters([...filters, filter]);
  };

  const removeFilter = (filter: Filter) => {
    setFilters(
      filters.filter(
        (item) => item.type !== filter.type || item.value !== filter.value,
      ),
    );
  };

  const onAddFilter = (
    option: SelectableOptionT | undefined,
    level?: string | SelectableOptionT,
    demographic?: string,
  ) => {
    const filter = toFilter(option, level, demographic);
    if (filter) addFilter(filter);
  };

  const hasResponses = !!culturePerception?.completedUsersCount;

  return (
    <Box
      sx={{
        backgroundColor: "black.3",
      }}
    >
      <Box
        sx={{
          marginLeft: "20px",
          maxWidth: "1375px",
          pl: 3,
        }}
      >
        {!!closedCampaigns?.length && selectedCampaign && (
          <Inline>
            <FiltersSelect
              onAddFilter={onAddFilter}
              selectedCampaign={toFilterableCampaign(selectedCampaign)}
              showIcon={true}
              buttonDisabled={!hasResponses && !filters.length}
            />
            <Inline>
              {filters.map((filter: Filter) => {
                return (
                  <FilterPill
                    key={`${filter.type}:${filter.value}`}
                    filter={filter}
                    removeFilter={removeFilter}
                  />
                );
              })}
              {filters.length > 0 && (
                <Button
                  look="naked"
                  iconLeft={CloseSolidIcon}
                  onClick={() => {
                    setFilters([]);
                  }}
                >
                  Clear all
                </Button>
              )}
            </Inline>
          </Inline>
        )}
      </Box>
      {selectedCampaign && (
        <EngagementResults
          culturePerception={culturePerception}
          failed={!!error}
          filtered={!!filters.length}
        />
      )}
      {!closedCampaigns?.length && (
        <NothingToSee description="There are no closed campaigns to report on." />
      )}
    </Box>
  );
};

/** The answers, or why there are none to show: the query failed, or nobody (matching) responded. */
const EngagementResults = ({
  culturePerception,
  failed,
  filtered,
}: {
  culturePerception?: CulturePerception;
  failed: boolean;
  filtered: boolean;
}) => {
  if (failed) {
    return (
      <NoticeCard>
        <ErrorMessage
          title="We couldn't load the engagement results"
          description="Something went wrong reading this campaign's survey data. Please try again."
        />
      </NoticeCard>
    );
  }

  if (culturePerception?.completedUsersCount) {
    return (
      <EngagementContent
        loading={false}
        culturePerception={culturePerception}
      ></EngagementContent>
    );
  }

  return (
    <NothingToSee
      description={
        filtered
          ? "Looks like there is no survey data for the filter selected."
          : "Looks like there is no survey data for this campaign."
      }
    />
  );
};

const NothingToSee = ({ description }: { description: string }) => (
  <NoticeCard>
    <ErrorMessage
      title="Nothing to see here!"
      icon={<NotFoundImage />}
      description={description}
    />
  </NoticeCard>
);

const NoticeCard = ({ children }: { children: JSX.Element }) => (
  <Card
    sx={{
      textAlign: "center",
      width: "100%",
      backgroundColor: "#FFFFFF",
    }}
  >
    {children}
  </Card>
);

/**
 * The campaign's engagement answers, narrowed by `filters`.
 *
 * Skipped without a perception assessment — orgs with no closed campaigns and legacy campaigns
 * have none, and the gateway rejects the query outright when `assessmentSk` is missing.
 */
const useCulturePerception = (
  perceptionAssessmentSk: string | undefined,
  filters: Filter[],
) => {
  const { platformFederationGatewayClient } = useApolloClients();
  const queryFilters = useMemo(
    () => toInsightsFilterInputs(filters),
    [filters],
  );

  const { data, loading, error } = useCulturePerceptionQuery({
    client: platformFederationGatewayClient,
    fetchPolicy: "cache-and-network",
    variables: {
      assessmentSk: perceptionAssessmentSk ?? "",
      // Left out when empty: the resolver takes any list, even `[]`, as filtered and then reads
      // every response a second time for the unfiltered total.
      filters: queryFilters.length ? queryFilters : undefined,
    },
    skip: !perceptionAssessmentSk,
  });

  return { culturePerception: data?.culturePerception, loading, error };
};

const findPerceptionAssessmentSk = (campaign?: Campaign) =>
  campaign?.assessments?.find(
    (assessment) => assessment.type === AssessmentType.Perception,
  )?.sk;

/** The picked filters grouped into the query's `[{ filterType, options }]`, empty groups left out. */
const toInsightsFilterInputs = (filters: Filter[]): InsightsFilterInput[] => {
  const valuesOfType = (type: FilterType) =>
    filters.filter((filter) => filter.type === type).map(({ value }) => value);

  return [
    {
      filterType: InsightsFilterType.DIVISIONS,
      options: valuesOfType(FilterType.Division),
    },
    {
      filterType: InsightsFilterType.OFFICE_LOCATION,
      options: valuesOfType(FilterType.OfficeLocation),
    },
    {
      filterType: InsightsFilterType.JOB_LEVEL,
      options: valuesOfType(FilterType.JobLevel),
    },
  ].filter((input) => input.options.length);
};

/**
 * The filter `FiltersSelect` asked for. A division is picked either as a leaf `option` or, with
 * "Select <division>", as the `level` being browsed.
 */
const toFilter = (
  option: SelectableOptionT | undefined,
  level: string | SelectableOptionT | undefined,
  demographic: string | undefined,
): Filter | undefined => {
  const type = demographic ? DEMOGRAPHIC_FILTER_TYPES[demographic] : undefined;
  if (!type) return undefined;

  if (type === FilterType.Division) return toDivisionFilter(option ?? level);

  if (!option) return undefined;
  return { name: option.label, type, value: option.label };
};

/** Divisions match on their path prefix, so the value is the path with a trailing `$`. */
const toDivisionFilter = (
  division: string | SelectableOptionT | undefined,
): Filter | undefined => {
  if (!division || typeof division === "string") return undefined;
  if (typeof division.path !== "string") return undefined;

  const path = division.path.startsWith("$")
    ? division.path.substring(1)
    : division.path;

  return { name: division.label, type: FilterType.Division, value: `${path}$` };
};

/** The campaign with only the demographics this tab can filter on. */
const toFilterableCampaign = (campaign: Campaign): FilterableCampaign => ({
  ...campaign,
  filters: {
    divisions: campaign.filters?.divisions,
    officeLocation: campaign.filters?.officeLocation,
    jobLevel: campaign.filters?.jobLevel,
  },
});

export default Engagement;
